import React, {useEffect, useState} from 'react';
import "./FormAccount.scss"
import {UpdateUserInfo, useFetchConfigUser} from "../../../../service/AccountService";
import {CallbackErrorModalFL, CallbackFinishedModalFL} from "../ModalForm";
import FCSpinner from "../../../FCObjets/FCSpinner";
import FormAccountUser, {InterfaceAccountUser} from "./FormAccountUser";
import FormAccountMailing, {InterfaceAccountMailing} from "./FormAccountMailing";
import FormAccountEnterprise, {InterfaceAccountEnterprise} from "./FormAccountEnterprise";
import FormAccountAdmin, {InterfaceAccountAdmin} from "./FormAccountAdmin";
import {getIdWithToken, isAdminWithToken} from "../../../../utils/storage/Token";
import {useToast} from "../../../FCObjets/FCToast/FCToast";

type FormAccountListProps = {
    onFinished: CallbackFinishedModalFL;
    onError: CallbackErrorModalFL;
    idUser: number;
    hideHeader?: boolean;
};

const TABS = ['Utilisateur', 'Entreprise', 'Mailing'] as const;
type TabKey = typeof TABS[number] | 'Admin';

const FormAccount: React.FC<FormAccountListProps> = ({idUser, onFinished, onError, hideHeader = false}) => {
    const toast = useToast();

    const [ongletActif, setOngletActif] = useState<TabKey>('Utilisateur');
    const {data: configUser, loading, error} = useFetchConfigUser(idUser);

    const [dataUser, setDataUser] = useState<InterfaceAccountUser>();
    const [dataMailing, setDataMailing] = useState<InterfaceAccountMailing>();
    const [dataEnterprise, setDataEnterprise] = useState<InterfaceAccountEnterprise>();
    const [dataAdmin, setDataAdmin] = useState<InterfaceAccountAdmin>();

    useEffect(() => {
        if (configUser) {
            setDataUser({
                civility: configUser.user.civility,
                firstname: configUser.user.firstname,
                lastname: configUser.user.lastname,
                titleJob: configUser.user.userInfo.titleJob,
                urlCV: configUser.user.userInfo.urlCV,
                contactEmail: configUser.user.contactEmail,
                phoneNumber: configUser.user.phoneNumber
            });
            setDataEnterprise({
                companyLocation: configUser.user.userInfo.companyLocation,
                companyName: configUser.user.userInfo.companyName,
                companySiret: configUser.user.userInfo.companySiret,
                declarationLocation: configUser.user.userInfo.declarationLocation,
                tjm: configUser.user.userInfo.tjmDefault,
                logo: configUser.user.userInfo.logo,
                vl: configUser.user.userInfo.vl,
                tvaNumber: configUser.user.userInfo.tvaNumber
            });
            setDataAdmin({
                role: configUser.user.role,
                stateAccount: configUser.user.stateAccount
            });
            setDataMailing({
                serverSMTP: configUser.user.userInfo.nameServerSMTP,
                userSmtp: configUser.user.userInfo.userSMTP,
                passSMTP: ""
            });
        }
        if (error) onError(error);
    }, [error, configUser]);

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const handleSubmit = () => {
        const errors: Record<string, string> = {};

        if (!dataUser?.firstname?.trim()) errors.firstname = "Le prénom est obligatoire.";
        if (!dataUser?.lastname?.trim()) errors.lastname = "Le nom est obligatoire.";
        if (dataUser?.contactEmail) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(dataUser.contactEmail)) errors.contactEmail = "L'adresse email n'est pas valide.";
        }
        if (!dataEnterprise?.companyName?.trim()) errors.companyName = "Le nom de l'entreprise est obligatoire.";
        if (dataEnterprise?.companySiret?.trim() && !/^\d{14}$/.test(dataEnterprise.companySiret.replace(/\s/g, ""))) {
            errors.companySiret = "Le SIRET doit contenir 14 chiffres.";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error(Object.values(errors).join(" "), "Validation");
            return;
        }
        setFieldErrors({});
        let userInfo: {} = {
            id: configUser.user.id,
            firstname: dataUser?.firstname,
            lastname: dataUser?.lastname,
            contactEmail: dataUser?.contactEmail,
            civility: dataUser?.civility,
            phoneNumber: dataUser?.phoneNumber,
            userInfo: {
                id: configUser.user.userInfo.id,
                userSMTP: dataMailing?.userSmtp,
                nameServerSMTP: dataMailing?.serverSMTP,
                passSMTP: dataMailing?.passSMTP !== "" ? dataMailing?.passSMTP : undefined,
                tjmDefault: dataEnterprise?.tjm,
                companyName: dataEnterprise?.companyName,
                companySiret: dataEnterprise?.companySiret,
                declarationLocation: dataEnterprise?.declarationLocation,
                companyLocation: dataEnterprise?.companyLocation,
                titleJob: dataUser?.titleJob,
                urlCV: dataUser?.urlCV,
                logo: dataEnterprise?.logo,
                vl: dataEnterprise?.vl,
                tvaNumber: dataEnterprise?.tvaNumber
            }
        };
        if (isAdminWithToken()) {
            userInfo = {...userInfo, role: dataAdmin?.role, stateAccount: dataAdmin?.stateAccount};
        }
        UpdateUserInfo(getIdWithToken(), userInfo).then(() => onFinished()).catch((err) => onError(err));
    };

    const tabs: TabKey[] = [...TABS, ...(isAdminWithToken() ? ['Admin' as TabKey] : [])];

    const panelTitles: Record<TabKey, string> = {
        Utilisateur: "Profil de l'intervenant",
        Entreprise: "Informations entreprise",
        Mailing: "Configuration mailing",
        Admin: "Administration du compte"
    };

    return (
        <div className="configPage">
            {!hideHeader && (
                <>
                    <div className="configPageLabel">Paramètres de compte</div>
                    <h1 className="configPageTitle">Configuration</h1>
                    <p className="configPageSubtitle">Personnalisez votre compte.</p>
                </>
            )}

            {/* Onglets */}
            <div className="configTabs">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        type="button"
                        className={`configTab${ongletActif === tab ? ' active' : ''}`}
                        onClick={() => setOngletActif(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Panneau principal */}
            <div className="configPanel">
                <div className="configPanelTitle">{panelTitles[ongletActif]}</div>

                {loading && <FCSpinner size="large"/>}

                <form className="configForm" onSubmit={(e) => e.preventDefault()}>
                    {dataUser && ongletActif === 'Utilisateur' && (
                        <FormAccountUser handlerChange={setDataUser} data={dataUser} fieldErrors={fieldErrors}/>
                    )}
                    {dataEnterprise && ongletActif === 'Entreprise' && (
                        <FormAccountEnterprise handlerChange={setDataEnterprise} data={dataEnterprise} fieldErrors={fieldErrors}/>
                    )}
                    {dataMailing && configUser && ongletActif === 'Mailing' && (
                        <FormAccountMailing handlerChange={setDataMailing} data={dataMailing}
                                            listServerSmtp={configUser.listServerSmtp}/>
                    )}
                    {isAdminWithToken() && dataAdmin && ongletActif === 'Admin' && (
                        <FormAccountAdmin handlerChange={setDataAdmin} data={dataAdmin}/>
                    )}

                    <div className="configActions">
                        <button type="button" className="configBtnPrimary" onClick={handleSubmit}>
                            <span className="material-symbols-outlined">save</span>
                            Enregistrer les modifications
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FormAccount;
